import { Driver } from '../context/model/types';

export const SLOT_TIMES = {
  morning: '9:00am-11:00am',
  afternoon: '1:00pm-3:00pm',
} as const;

export type SlotKey = keyof typeof SLOT_TIMES;

export interface SlotConfig {
  enabled: boolean;
  paid: boolean;
  capacity?: number;
}

export interface DayConfig {
  morning: SlotConfig;
  afternoon: SlotConfig;
}

export type DriverSchedule = Record<string, DayConfig | null>;

export const DEFAULT_SLOT: SlotConfig = { enabled: false, paid: false, capacity: 1 };
export const DEFAULT_DAY: DayConfig = {
  morning: { ...DEFAULT_SLOT },
  afternoon: { ...DEFAULT_SLOT },
};

export function parseSchedule(json: string | null | undefined): DriverSchedule {
  if (!json) return {};
  try {
    return JSON.parse(json) as DriverSchedule;
  } catch {
    return {};
  }
}

export interface AvailableSlot {
  isPaid: boolean;
}

export function getAvailableSlots(
  drivers: Driver[],
  appointments: { driver: string; date: string; time: string; type?: string }[],
  date: string,
): Partial<Record<SlotKey, AvailableSlot>> {
  // Use noon to avoid DST edge cases
  const dow = String(new Date(date + 'T12:00:00').getDay());
  const result: Partial<Record<SlotKey, AvailableSlot>> = {};

  for (const driver of drivers) {
    const schedule = parseSchedule(driver.availability);
    const dayConfig = schedule[dow];

    for (const slotKey of ['morning', 'afternoon'] as SlotKey[]) {
      const slotCfg = dayConfig?.[slotKey];
      const slotTime = SLOT_TIMES[slotKey];

      const slotAppts = appointments.filter(
        a => a.date === date && a.driver === driver.name && a.time === slotTime,
      );
      const hasBlock = slotAppts.some(a => a.type === 'Block');
      const hasOpen = slotAppts.some(a => a.type === 'Open');
      const bookings = slotAppts.filter(a => a.type !== 'Block' && a.type !== 'Open').length;

      if (hasBlock) continue;

      const baseCapacity = slotCfg?.enabled ? (slotCfg.capacity ?? 1) : 0;
      const effectiveCapacity = baseCapacity + (hasOpen ? 1 : 0);
      if (effectiveCapacity === 0 || bookings >= effectiveCapacity) continue;

      // No schedule config means the slot is only bookable via an Open
      // override (overflow / third-party mover) — always treat that as paid.
      const isPaid = slotCfg?.paid ?? true;
      const existing = result[slotKey];
      // Slot is free if any driver with remaining capacity offers it free
      if (!existing || (existing.isPaid && !isPaid)) {
        result[slotKey] = { isPaid };
      }
    }
  }

  return result;
}

export function assignDriver(
  drivers: Driver[],
  appointments: { driver: string; date: string; time: string; type?: string }[],
  date: string,
  slot: SlotKey,
): Driver | null {
  const dow = String(new Date(date + 'T12:00:00').getDay());
  const slotTime = SLOT_TIMES[slot];

  const candidates: { driver: Driver; paid: boolean; dayCount: number }[] = [];

  for (const driver of drivers) {
    const schedule = parseSchedule(driver.availability);
    const dayConfig = schedule[dow];
    const slotCfg = dayConfig?.[slot];

    const slotAppts = appointments.filter(
      a => a.date === date && a.driver === driver.name && a.time === slotTime,
    );
    const hasBlock = slotAppts.some(a => a.type === 'Block');
    const hasOpen = slotAppts.some(a => a.type === 'Open');
    const bookings = slotAppts.filter(a => a.type !== 'Block' && a.type !== 'Open').length;

    if (hasBlock) continue;

    const baseCapacity = slotCfg?.enabled ? (slotCfg.capacity ?? 1) : 0;
    const effectiveCapacity = baseCapacity + (hasOpen ? 1 : 0);
    if (effectiveCapacity === 0 || bookings >= effectiveCapacity) continue;

    const dayCount = appointments.filter(
      a => a.date === date && a.driver === driver.name && a.type !== 'Block' && a.type !== 'Open',
    ).length;
    candidates.push({ driver, paid: slotCfg?.paid ?? true, dayCount });
  }

  if (candidates.length === 0) return null;

  // Prefer free drivers; break ties by fewest appointments that day
  candidates.sort((a, b) => {
    if (a.paid !== b.paid) return a.paid ? 1 : -1;
    return a.dayCount - b.dayCount;
  });

  return candidates[0].driver;
}
